import {
  PathNavigationCoordinator,
  SelectionSyncCoordinator,
} from '@trimble/pane-sync';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { CapabilityRegistry } from '@trimble/web3d';
import { getTrbModelName } from '@trimble/web3d-providers';

/**
 * Records the picked trajectory/path as the active alignment on the
 * {@link PathNavigationCoordinator}.  The coordinator's reactive effect
 * starts navigation on every pane currently in path-navigation or
 * cross-section view mode (and on panes mounted later in those modes).
 *
 * @param capabilityRegistry - The capability registry to get pane capabilities from
 * @param modelId - The model ID to start navigation on
 */
export const setActiveAlignment = (
  capabilityRegistry: CapabilityRegistry,
  modelId: string
): void => {
  const trbModelName = getTrbModelName(modelId);

  const coordinator = capabilityRegistry.get(PathNavigationCoordinator)();
  coordinator?.setActiveAlignmentModelId(trbModelName);
};

/**
 * Syncs the clicked tree node to the viewer selection. Calls hierarchy-root
 * selection on normal click, and add-to-selection hierarchy-root when Ctrl or
 * Shift is held. No-op if the coordinator is missing, the node is not a leaf,
 * disabled, or not visible in the viewer.
 *
 * @param modelId - Model ID to select in the viewer
 * @param node - The tree node that was clicked
 * @param e - Mouse event (used to detect Ctrl/Shift)
 * @param isNodeVisible - Whether the node's data is loaded and visible in the viewer
 * @param selectionSyncCoordinator - Coordinator that keeps tree and viewer selection in sync
 */
export const handleNodeSelectionSync = async <T>(
  modelId: string,
  node: TreeNodeDefinition<T>,
  e: MouseEvent,
  isNodeVisible: (nodeId: string) => boolean,
  selectionSyncCoordinator?: SelectionSyncCoordinator
): Promise<void> => {
  if (
    !selectionSyncCoordinator ||
    !node.isLeaf ||
    node.status?.disabled ||
    !isNodeVisible(node.id)
  )
    return;

  if (e.ctrlKey || e.shiftKey) {
    await selectionSyncCoordinator.addToSelectionHierarchyRoots(modelId);
  } else {
    await selectionSyncCoordinator.setSelectionHierarchyRoots(modelId);
  }
};
